import { useState, useEffect } from 'react';
import { classesOf } from '@machina/core';
import { useAuthStore, getUserDisplayName } from '../../stores/authStore';
import { useCampaignStore, Campaign } from '../../stores/campaignStore';
import { useCharacterStore } from '../../stores/characterStore';
import { SectionHeader } from '../Ornaments';
import { Icon } from '../Icon';
import { CreateCampaignModal } from '../CreateCampaignModal';

interface ProfilePageProps {
  onClose: () => void;
}

export function ProfilePage({ onClose }: ProfilePageProps) {
  const { user, updateDisplayName, isLoading, error, clearError } = useAuthStore();
  const { campaigns, loadCampaigns } = useCampaignStore();
  const { characters, loadFromSupabase } = useCharacterStore();

  const [displayName, setDisplayName] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showCreateCampaign, setShowCreateCampaign] = useState(false);

  // Initialize display name from user
  useEffect(() => {
    if (user) {
      setDisplayName(getUserDisplayName(user));
    }
  }, [user]);

  // Load campaigns and characters
  useEffect(() => {
    if (user?.id) {
      loadCampaigns(user.id);
      loadFromSupabase(user.id);
    }
  }, [user?.id, loadCampaigns, loadFromSupabase]);

  const handleSave = async () => {
    clearError();
    setSaveSuccess(false);
    const result = await updateDisplayName(displayName);
    if (!result.error) {
      setSaveSuccess(true);
      setIsEditing(false);
      setTimeout(() => setSaveSuccess(false), 3000);
    }
  };

  const dmCampaigns = campaigns.filter((c) => c.role === 'dm');
  const playerCampaigns = campaigns.filter((c) => c.role === 'player');
  const characterList = Array.from(characters.values());

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <h2 style={styles.title}>Profile</h2>
          <button onClick={onClose} style={styles.closeButton}>
            &times;
          </button>
        </div>

        {/* Content */}
        <div style={styles.content}>
          {/* Display Name Section */}
          <section style={styles.section}>
            <SectionHeader title="Display Name" />
            {error && <div style={styles.error}>{error}</div>}
            {saveSuccess && <div style={styles.success}>Saved!</div>}

            <div style={styles.nameRow}>
              {isEditing ? (
                <>
                  <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    style={styles.input}
                    autoFocus
                  />
                  <button
                    onClick={handleSave}
                    disabled={isLoading || !displayName.trim()}
                    style={styles.saveButton}
                  >
                    {isLoading ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={() => {
                      setIsEditing(false);
                      setDisplayName(getUserDisplayName(user));
                    }}
                    style={styles.cancelButton}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <span style={styles.displayName}>{displayName}</span>
                  <button onClick={() => setIsEditing(true)} style={styles.editButton}>
                    Edit
                  </button>
                </>
              )}
            </div>
            <p style={styles.email}>{user?.email}</p>
          </section>

          {/* Campaigns Section */}
          <section style={styles.section}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--spacing-sm)' }}>
              <SectionHeader title="Campaigns" />
              <button
                onClick={() => setShowCreateCampaign(true)}
                style={styles.newCampaignButton}
              >
                <Icon name="scroll" size={14} />
                New Campaign
              </button>
            </div>

            {/* DM Campaigns */}
            {dmCampaigns.length > 0 && (
              <div style={styles.subsection}>
                <h4 style={styles.subsectionTitle}>Running as DM</h4>
                {dmCampaigns.map((campaign) => (
                  <CampaignCard key={campaign.id} campaign={campaign} />
                ))}
              </div>
            )}

            {/* Player Campaigns */}
            {playerCampaigns.length > 0 && (
              <div style={styles.subsection}>
                <h4 style={styles.subsectionTitle}>Playing In</h4>
                {playerCampaigns.map((campaign) => (
                  <CampaignCard key={campaign.id} campaign={campaign} />
                ))}
              </div>
            )}

            {campaigns.length === 0 && (
              <p style={styles.emptyText}>No campaigns yet. Create or join one to get started!</p>
            )}
          </section>

          {/* Characters Section */}
          <section style={styles.section}>
            <SectionHeader title="Characters" />
            {characterList.length > 0 ? (
              <div style={styles.characterGrid}>
                {characterList.map((char) => (
                  <div key={char.id} style={styles.characterCard}>
                    <div style={styles.characterName}>{char.name}</div>
                    <div style={styles.characterInfo}>
                      {/* Via the accessor: a row from a classless system has
                          no `classes` key, and `.map` on it threw. Classless
                          characters show their race alone. */}
                      {classesOf(char).map((c, i) => (
                        <span key={c.name}>
                          {i > 0 && ' / '}
                          Level {c.level} {c.name}
                        </span>
                      ))}
                      {' '}{char.race}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p style={styles.emptyText}>No characters yet. Create one in a campaign!</p>
            )}
          </section>
        </div>
      </div>

      {/* Create Campaign Modal */}
      {showCreateCampaign && (
        <CreateCampaignModal
          onClose={() => setShowCreateCampaign(false)}
          onCreated={() => {
            // Reload campaigns after creating
            if (user?.id) {
              loadCampaigns(user.id);
            }
          }}
        />
      )}
    </div>
  );
}

function CampaignCard({ campaign }: { campaign: Campaign }) {
  return (
    <div style={styles.campaignCard}>
      <div style={styles.campaignName}>{campaign.name}</div>
      {campaign.description && (
        <div style={styles.campaignDesc}>{campaign.description}</div>
      )}
      <div style={styles.campaignMeta}>
        <span style={styles.roleBadge}>
          {campaign.role === 'dm' ? 'DM' : 'Player'}
        </span>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },

  modal: {
    width: '100%',
    maxWidth: '600px',
    maxHeight: '80vh',
    backgroundColor: 'var(--color-surface)',
    borderRadius: 'var(--radius-lg)',
    border: '2px solid var(--color-accent)',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },

  header: {
    padding: 'var(--spacing-md) var(--spacing-lg)',
    borderBottom: '1px solid var(--color-border)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },

  title: {
    margin: 0,
    fontSize: 'var(--font-size-lg)',
    fontWeight: 600,
    color: 'var(--color-accent)',
    letterSpacing: '0.1em',
  },

  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    color: 'var(--color-text-secondary)',
    cursor: 'pointer',
    padding: 'var(--spacing-xs)',
    lineHeight: 1,
  },

  content: {
    padding: 'var(--spacing-lg)',
    overflowY: 'auto',
    flex: 1,
  },

  section: {
    marginBottom: 'var(--spacing-xl)',
  },

  error: {
    padding: 'var(--spacing-sm)',
    backgroundColor: 'rgba(220, 53, 69, 0.15)',
    border: '1px solid var(--color-error)',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--color-error)',
    fontSize: 'var(--font-size-sm)',
    marginBottom: 'var(--spacing-sm)',
  },

  success: {
    padding: 'var(--spacing-sm)',
    backgroundColor: 'rgba(40, 167, 69, 0.15)',
    border: '1px solid #28a745',
    borderRadius: 'var(--radius-sm)',
    color: '#28a745',
    fontSize: 'var(--font-size-sm)',
    marginBottom: 'var(--spacing-sm)',
  },

  nameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-sm)',
    marginTop: 'var(--spacing-sm)',
  },

  displayName: {
    fontSize: 'var(--font-size-lg)',
    fontWeight: 600,
    color: 'var(--color-text)',
  },

  email: {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-secondary)',
    marginTop: 'var(--spacing-xs)',
  },

  input: {
    flex: 1,
    padding: 'var(--spacing-sm)',
    backgroundColor: 'var(--color-background)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--color-text)',
    fontSize: 'var(--font-size-md)',
  },

  editButton: {
    padding: 'var(--spacing-xs) var(--spacing-sm)',
    backgroundColor: 'transparent',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--color-text-secondary)',
    fontSize: 'var(--font-size-xs)',
    cursor: 'pointer',
  },

  saveButton: {
    padding: 'var(--spacing-xs) var(--spacing-md)',
    backgroundColor: 'var(--color-accent)',
    border: 'none',
    borderRadius: 'var(--radius-sm)',
    color: '#1a1410',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 600,
    cursor: 'pointer',
  },

  cancelButton: {
    padding: 'var(--spacing-xs) var(--spacing-md)',
    backgroundColor: 'transparent',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--color-text-secondary)',
    fontSize: 'var(--font-size-sm)',
    cursor: 'pointer',
  },

  subsection: {
    marginTop: 'var(--spacing-md)',
  },

  subsectionTitle: {
    fontSize: 'var(--font-size-sm)',
    fontWeight: 600,
    color: 'var(--color-text-secondary)',
    margin: 0,
    marginBottom: 'var(--spacing-sm)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },

  campaignCard: {
    padding: 'var(--spacing-sm) var(--spacing-md)',
    backgroundColor: 'var(--color-background)',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--color-border)',
    marginBottom: 'var(--spacing-sm)',
  },

  campaignName: {
    fontWeight: 600,
    color: 'var(--color-text)',
  },

  campaignDesc: {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-secondary)',
    marginTop: 'var(--spacing-xs)',
  },

  campaignMeta: {
    marginTop: 'var(--spacing-xs)',
    display: 'flex',
    gap: 'var(--spacing-xs)',
  },

  roleBadge: {
    fontSize: 'var(--font-size-xs)',
    padding: '2px 6px',
    borderRadius: 'var(--radius-xs)',
    backgroundColor: 'var(--color-accent)',
    color: '#1a1410',
    fontWeight: 600,
  },

  emptyText: {
    color: 'var(--color-text-secondary)',
    fontSize: 'var(--font-size-sm)',
    fontStyle: 'italic',
    textAlign: 'center',
    padding: 'var(--spacing-md)',
  },

  newCampaignButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-xs)',
    padding: 'var(--spacing-xs) var(--spacing-md)',
    fontSize: 'var(--font-size-sm)',
    fontFamily: 'var(--font-display)',
    fontWeight: 600,
    background: 'linear-gradient(180deg, #d4af37 0%, #a08030 50%, #8b6914 100%)',
    border: '1px solid #d4af37',
    borderRadius: 'var(--radius-sm)',
    color: '#1a1410',
    cursor: 'pointer',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
  },

  characterGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: 'var(--spacing-sm)',
    marginTop: 'var(--spacing-sm)',
  },

  characterCard: {
    padding: 'var(--spacing-md)',
    backgroundColor: 'var(--color-background)',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--color-border)',
  },

  characterName: {
    fontWeight: 600,
    color: 'var(--color-text)',
    marginBottom: 'var(--spacing-xs)',
  },

  characterInfo: {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-secondary)',
  },
};
